/**
 * ModalSlideout - Accessible slide-out modal custom element.
 * Overlay, scroll lock, Escape, and triggers are handled by slideout-manager.js.
 */

class ModalSlideout extends HTMLElement {
  static overlay = null;

  static getSharedOverlay() {
    return window.SlideoutManager?.getSharedOverlay() ?? null;
  }

  static syncOverlayVisibility() {
    window.SlideoutManager?.sync();
  }

  constructor() {
    super();
    this.isOpen = false;
    this.triggerElement = null;
    this._outsideClickHandler = null;
  }

  connectedCallback() {
    // Defer initialization to a microtask so that frameworks (e.g. Preact) can
    // finish rendering children before moveToBody() relocates this element.
    // Without this, moveToBody() fires mid-render and causes insertBefore errors
    // in app extensions that render <modal-slideout> from a virtual DOM.
    queueMicrotask(() => this.bindEvents());
  }

  disconnectedCallback() {
    if (this._outsideClickHandler) {
      document.removeEventListener('pointerdown', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }

    if (this.isOpen || this.hasAttribute('open')) {
      this.isOpen = false;
      window.SlideoutManager?.sync();
    }
  }

  /**
   * Re-initializes the focus trap against the dialog's current DOM.
   * Call this after a consumer injects content asynchronously (e.g. content
   * loaded after open() already ran) — trapFocus snapshots focusable
   * elements at call time, so callers must re-arm it whenever the set of
   * focusable elements inside the dialog changes materially.
   */
  refreshFocusTrap() {
    if (!this.isOpen) return;
    const focusRoot = this.viewModel?.$dialog ?? this;
    window.utilities?.removeTrapFocus?.(this.triggerElement);
    window.utilities?.trapFocus?.(this, focusRoot);
  }

  validateStructure() {
    if (!this.querySelector('.slideout')) {
      console.warn(
        `modal-slideout (id="${this.id}") is missing a .slideout wrapper — triggers and open() will not work.`,
      );
      return false;
    }
    return true;
  }

  moveToBody() {
    const portalToBody = this.hasAttribute('data-portal-body');
    if (!portalToBody && !window.utilities?.isMobileBreakpoint?.() && this.animation === 'slide-bottom') {
      return;
    }
    window.SlideoutManager?.insertHostBeforeOverlay(this);
  }

  getViewModel() {
    return {
      $dialog: this.querySelector('.slideout[role="dialog"], .slideout'),
      $closeBtn: this.querySelectorAll('.close-btn'),
    };
  }

  initializeAttributes() {
    this.closeOnEsc = this.getAttribute('close-on-esc') !== 'false';
    this.animation = this.getAttribute('animation') || 'slide-right';
  }

  syncAriaOpen(isOpen) {
    this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    const dialog = this.querySelector('.slideout[role="dialog"], .slideout');
    if (dialog) dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
  }

  bindEvents() {
    if (!this.validateStructure()) return;

    this.removeAttribute('hidden');
    this.initializeAttributes();
    this.moveToBody();
    window.SlideoutManager?.getSharedOverlay();
    this.syncAriaOpen(false);

    this.viewModel = this.getViewModel();

    if (this.hasAttribute('open')) {
      this.open();
    }

    this.viewModel.$closeBtn?.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    window.utilities?.NavigationKeyHandler?.setup({
      closeModal: () => this.isOpen && this.close(),
    });
  }

  open(trigger) {
    this.triggerElement = trigger ?? this.triggerElement;

    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'true');
    }

    this.closeAll(this.id);
    this.setAttribute('open', '');
    this.isOpen = true;
    this.syncAriaOpen(true);
    window.SlideoutManager?.sync();

    const focusRoot = this.viewModel?.$dialog ?? this;
    window.utilities?.trapFocus?.(this, focusRoot);

    this._outsideClickHandler = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('pointerdown', this._outsideClickHandler);
  }

  close(id = null) {
    if (this._outsideClickHandler) {
      document.removeEventListener('pointerdown', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }

    if (id && this.id === id) return;

    this.removeAttribute('open');
    this.isOpen = false;
    this.syncAriaOpen(false);
    window.SlideoutManager?.sync();

    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'false');
      this.triggerElement.classList.remove('is-active');
      try {
        this.triggerElement.focus({ preventScroll: true });
      } catch (_) {
        /* noop */
      }
    }

    window.utilities?.removeTrapFocus?.(this.triggerElement);
  }

  disableAnimationTemporary() {
    this.classList.add('no-animation');
    setTimeout(() => this.classList.remove('no-animation'), 400);
  }

  closeAll(id = null) {
    const openModals = document.querySelectorAll('modal-slideout[open]');
    if (!openModals.length) return;

    if (window.utilities?.isMobileBreakpoint?.()) {
      openModals.forEach((modal) => modal.disableAnimationTemporary?.());
    }

    openModals.forEach((modal) => modal.close(id));
  }
}

customElements.define('modal-slideout', ModalSlideout);

// Ensure manager is loaded (defer order: slideout-manager before this file)
if (window.SlideoutManager) {
  ModalSlideout.overlay = window.SlideoutManager.getSharedOverlay();
}
